import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { Download, FileSignature } from "lucide-react";
import type { ContractItemDTO } from "@/modules/document-portal/domain/contracts";
import { formatDateLocal } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";

const STATUS_LABELS: Record<string, string> = {
  signed: "ລົງນາມແລ້ວ",
  pending_signature: "ລໍຖ້າລົງນາມ",
  expired: "ຫມົດອາຍຸແລ້ວ",
  draft: "ຮ່າງ",
};

type ContractDocumentCardProps = {
  item: ContractItemDTO;
  onView?: (id: string) => void;
  onDownload?: (id: string) => void;
};

export function ContractDocumentCard({
  item,
  onDownload,
  onView,
}: ContractDocumentCardProps) {
  const canDownload = Boolean(resolveImageSrc(item.fileKey));
  const isSigned = item.status === "signed";
  const isPending = item.status === "pending_signature";
  const dateRange = `${item.startDate ? formatDateLocal(item.startDate) : "—"} – ${
    item.endDate ? formatDateLocal(item.endDate) : "—"
  }`;

  return (
    <Card className="overflow-hidden transition-colors hover:border-black/30">
      <CardContent className="p-4 py-0">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-black/5">
              <FileSignature className="size-5 text-black" />
            </div>
            <div className="min-w-0">
              <h4 className="truncate font-semibold text-base">{item.title}</h4>
              <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                {item.contractType}
              </p>
            </div>
          </div>
          <Badge
            variant={isSigned ? "default" : isPending ? "secondary" : "outline"}
            className={
              isSigned
                ? "shrink-0 bg-black"
                : isPending
                  ? "shrink-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                  : "shrink-0"
            }
          >
            {STATUS_LABELS[item.status] ?? item.status}
          </Badge>
        </div>

        <div className="mb-4 min-h-[2.5rem]">
          <p className="text-muted-foreground text-xs leading-relaxed">
            {dateRange}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 border-black font-medium hover:bg-black hover:text-white"
            onClick={() => onView?.(item.id)}
          >
            ເບິ່ງ
          </Button>
          {onDownload ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canDownload}
              title={
                canDownload
                  ? undefined
                  : "ບໍ່ມີໄຟລ໌ສັນຍາໃຫ້ດາວໂຫລດ (ຫຼື ຍັງບໍ່ໄດ້ອັບໂຫລດເອກະສານ)"
              }
              onClick={() => {
                if (!canDownload) return;
                onDownload(item.id);
              }}
            >
              <Download className="mr-1 size-4" />
              ດາວໂຫລດ
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
